import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import SummaryCardImage from '../SummaryCardImage'

import OnlyIfChildren from 'components/Common/OnlyIfChildren'
import TextButton from 'LuxKit/Button/TextButton'
import LineNotesIcon from 'LuxKit/Icons/line/LineNotesIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import useNavigateTo from 'hooks/useNavigateTo'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import SavedItemRemoveButton from 'tripPlanner/components/ItemCard/Common/SavedItemRemoveButton'
import { AddRemoveItineraryButton } from 'tripPlanner/components/Itinerary/AddRemoveItineraryButton'
import ItineraryDateButton from 'tripPlanner/components/Itinerary/ItineraryDateButton'
import EditTripItem from 'tripPlanner/components/TripModal/TripItems/Edit'
import TripItemSummary from 'tripPlanner/components/TripModal/TripItems/Summary'
import EditTripDates from 'tripPlanner/components/TripModal/Trips/EditTripDatesTP'
import TripContext from 'tripPlanner/contexts/TripContext'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { ItemMetadata } from 'tripPlanner/models'
import type { GeoPlace } from 'tripPlanner/types/common'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { isCustomItem } from 'tripPlanner/utils'
import AboveCardCloseButton from '../Common/AboveCardCloseButton'
import PlaceActionChips from 'tripPlanner/components/PlaceActionChips'
import GoogleEditorialSummary from 'tripPlanner/components/GoogleEditorialSummary'
import GooglePlacePriceLevel, { shouldShowPriceLevel } from 'tripPlanner/components/GooglePlacePriceLevel'
import BodyText from 'LuxKit/Typography/BodyText'
import HotelInclusionLabel from 'tripPlanner/components/ItemCard/HotelInclusionLabel'

const CardContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-around;
`

const Container = styled.div`
  position: relative;
  width: 100%;
  background-color: white;
  max-width: ${rem(300)};
  pointer-events: auto;
  box-shadow: ${props => props.theme.shadow.bottom.medium};

  ${mediaQueryUp.largeDesktop} {
    max-width: ${rem(700)};
  }
`
const ImageContainer = styled.div`
  width: 100%;

  > * {
    height: 100%;
    aspect-ratio: 16/9;
  }

  ${mediaQueryUp.largeDesktop} {
    width: 33%;
  }
`

const ContentWrapper = styled.div`
  padding: ${rem(16)};
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  gap: ${rem(8)};

  ${mediaQueryUp.largeDesktop} {
    padding: ${rem(24)};
  }
`

const MainGroup = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  ${mediaQueryUp.largeDesktop} {
    flex-direction: row-reverse;
    justify-content: space-between;
  }
`

interface Props {
  className?: string
  item: TripItem
  showCloseButton?: boolean
  onClose?: () => void
  offerImageId?: string
  imageURL?: string
  offerLink?: string
  rating?: { rating?: number, userRatingCount?: number }
  place: GeoPlace
  priceAreaContent?: React.ReactNode
  ctaAreaContent?: React.ReactNode
  locationDescriptionContent?: React.ReactNode
  descriptors?: Array<string | undefined>
  propertyDescription?: string
}

const BookmarkButtonContainer = styled(Group)`
  position: absolute;
  top: ${rem(12)};
  right: ${rem(12)};
`

const BaseTripItemSummaryCard = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      item,
      showCloseButton,
      onClose,
      offerImageId,
      imageURL,
      offerLink,
      rating,
      place,
      priceAreaContent,
      ctaAreaContent,
      locationDescriptionContent,
      descriptors = EmptyArray,
      propertyDescription,
    },
    ref,
  ) => {
    const openModal = useOpenModal()
    const navigateTo = useNavigateTo()
    const canEdit = useCanEditTrip()
    const tripContext = useContext(TripContext)

    const onClickAddTripItemDates = useCallback(() => {
      if (offerLink) {
        navigateTo(offerLink + '#date-picker')
      } else if (isCustomItem(item)) {
        openModal(EditTripItem, { item })
      }
    }, [item, navigateTo, offerLink, openModal])

    const onRequestAddTripDates = useCallback(
      (
        onDatesAdded?: (
          startDate: moment.Moment,
          endDate: moment.Moment,
        ) => void,
      ) => {
        openModal(EditTripDates, { tripId: item.tripId, onDatesAdded })
      },
      [item.tripId, openModal],
    )

    const descriptorsString = nonNullable(descriptors)
      .filter((text) => text.length > 0)
      .join(' · ')
    const isMobileScreen = useScreenSizeOnly('mobile')

    const openDetailsModal = useMemo(() => {
      const editorialSummary = place.editorialSummary
      return () => openModal(TripItemSummary, { item, editorialSummary })
    }, [item, place, openModal])

    const intl = useIntl()
    const { icon: Icon, singularName } = ItemMetadata[item.type]
    const typeName = intl.formatMessage(singularName)
    const isCustom = isCustomItem(item)
    const heading = 'name' in item && item.name ? item.name : place.name
    const hasImage = !!offerImageId || !!imageURL

    return (
      <CardContainer className={className}>
        <Container ref={ref}>
          {showCloseButton && (
            <AboveCardCloseButton onClick={onClose} />
          )}
          <MainGroup onClick={isMobileScreen ? openDetailsModal : undefined}>
            {hasImage && (
              <ImageContainer>
                <SummaryCardImage
                  place={place}
                  tripId={item.tripId}
                  tripItemId={item.id}
                  imgId={offerImageId}
                  imgURL={imageURL}
                />
              </ImageContainer>
            )}
            <ContentWrapper>
              <Group direction="vertical" gap={8} fullWidth>
                <Group
                  direction="horizontal"
                  horizontalAlign="space-between"
                  fullWidth
                >
                  <BodyTextBlock
                    variant="small"
                    startIcon={<Icon size="XS" />}
                    wrap="truncate"
                  >
                    {typeName}
                  </BodyTextBlock>
                </Group>
                <OnlyIfChildren
                  as={Group}
                  direction="horizontal"
                  gap={4}
                  verticalAlign="center"
                >
                  {locationDescriptionContent}
                </OnlyIfChildren>
                {place.region && (
                  <BodyTextBlock variant="small">{place.region}</BodyTextBlock>
                )}
                <Group direction="vertical" gap={4}>
                  <HeadingTextBlock lineClamp={2} variant="heading6">
                    {heading}
                  </HeadingTextBlock>
                  {propertyDescription && (
                    <CaptionTextBlock variant="medium">
                      {propertyDescription}
                    </CaptionTextBlock>
                  )}
                </Group>
                <Group direction="horizontal" verticalAlign="center" gap={8} wrap="wrap">
                  {rating?.rating && (
                    <GooglePlaceRatingDisplay
                      rating={rating.rating}
                      ratingsCount={rating.userRatingCount}
                      size="S"
                    />
                  )}
                  {rating?.rating && shouldShowPriceLevel(place.priceLevel) && (
                    <BodyText variant="medium" colour="neutral-five" align="center"> |</BodyText>
                  )}
                  {shouldShowPriceLevel(place.priceLevel) && (
                    <GooglePlacePriceLevel priceLevel={place.priceLevel} size="small" />
                  )}
                  {(rating?.rating || shouldShowPriceLevel(place.priceLevel)) && item.isHotelInclusion &&
                    <BodyText variant="medium" colour="neutral-five" align="center"> |</BodyText>}
                  {item.isHotelInclusion && <HotelInclusionLabel />}
                </Group>
                {place.editorialSummary && (
                  <GoogleEditorialSummary editorialSummary={place.editorialSummary} lineClamp={3} />
                )}
              </Group>
              {descriptorsString && (
                <BodyTextBlock
                  variant="medium"
                  weight="normal"
                  startIcon={<LineNotesIcon size="S" />}
                >
                  {descriptorsString}
                </BodyTextBlock>
              )}
              <Group direction="horizontal" horizontalAlign="space-between" fullWidth>
                {isCustom && tripContext?.displayContext !== 'search' && (
                  <TextButton
                    kind="tertiary"
                    size="small"
                    variant="default"
                    horizontalOutdent="start"
                    nonInteractable
                    onClick={openDetailsModal}
                  >
                    {intl.formatMessage({
                      id: 'P11ZAcZWac',
                      defaultMessage: 'View details',
                      description: 'Button to view details of a custom trip item on the map summary card',
                    })}
                  </TextButton>
                )}
                <PlaceActionChips
                  place={place}
                />
              </Group>
              <OnlyIfChildren
                as={Group}
                direction="horizontal"
                verticalAlign="end"
                horizontalAlign="space-between"
                fullWidth
              >
                {priceAreaContent && priceAreaContent}
                {ctaAreaContent && ctaAreaContent}
              </OnlyIfChildren>
            </ContentWrapper>
          </MainGroup>
          <BookmarkButtonContainer
            direction="horizontal"
            horizontalAlign="end"
            verticalAlign="center"
            gap={8}
            noDisplayWhenEmpty
          >
            {'isBooked' in item && item.isBooked && <BookedLabel />}
            {canEdit && !item.isSuggested && (
              <>
                {canEdit && isCustom && tripContext?.displayContext !== 'search' && (
                  <ItineraryDateButton
                    item={item}
                    variant="chip"
                    onRequestAddTripDates={onRequestAddTripDates}
                    placement="top"
                  />
                )}
                {canEdit && !isCustom && tripContext?.displayContext !== 'search' && (
                  <AddRemoveItineraryButton
                    item={item}
                    onClickAddTripItemDates={onClickAddTripItemDates}
                  />
                )}
                <SavedItemRemoveButton item={item} />
              </>
            )}
          </BookmarkButtonContainer>
        </Container>
      </CardContainer>
    )
  },
)

BaseTripItemSummaryCard.displayName = 'BaseTripItemSummaryCard'
export default BaseTripItemSummaryCard
